import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  Link,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import {
  CheckSIcon,
  ChevronLeftIcon,
  EmailOIcon,
} from '@tonic-one/react-icons';
import { t } from 'i18next';
import { MailRenderer } from '@Components/MailRenderer';
import { MessageHeader } from '../../type';

interface EmailData {
  msgSubject: string;
  timestamp: string;
  sender: string;
  recipient: string;
  reporter: string;
  messageId: string;
  attachments: { filename?: string }[];
  mailPreviewContent: string;
  mailHtmlContent: string;
  messageHeaders?: MessageHeader[];
}

interface EmailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  emailData: EmailData;
  formattedTimestamp: string | null;
}

const MAX_DISPLAYED_ATTACHMENTS = 10;

const truncateFilename = (filename: string): string => {
  if (filename.length <= 20) return filename;

  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) {
    return `${filename.substring(0, 10)}...${filename.substring(filename.length - 10)}`;
  }

  const extension = filename.substring(lastDotIndex);
  const nameWithoutExtension = filename.substring(0, lastDotIndex);

  if (nameWithoutExtension.length <= 10) {
    return filename;
  }

  const endLength = Math.min(10, nameWithoutExtension.length - 10);
  return `${nameWithoutExtension.substring(0, 10)}...${nameWithoutExtension.substring(nameWithoutExtension.length - endLength)}${extension}`;
};

interface InfoRowProps {
  label: string;
  value: string;
  secondaryColor: string;
}

const InfoRow = ({ label, value, secondaryColor }: InfoRowProps) => (
  <Flex>
    <Text color={secondaryColor} width="90px">
      {label}:
    </Text>
    <Text ml="2x" wordBreak="break-word" whiteSpace="normal" flex="1">
      {value || t('operations.euq.email.notAvailable')}
    </Text>
  </Flex>
);

interface AttachmentListProps {
  attachments: { filename?: string }[];
}

const AttachmentList = ({ attachments }: AttachmentListProps) => {
  const validAttachments = useMemo(
    () => attachments.filter(({ filename }) => !!filename),
    [attachments]
  );
  const displayedAttachments = validAttachments.slice(
    0,
    MAX_DISPLAYED_ATTACHMENTS
  );
  const hasMore = validAttachments.length > MAX_DISPLAYED_ATTACHMENTS;

  if (validAttachments.length === 0) {
    return <Text>-</Text>;
  }

  return (
    <>
      {displayedAttachments.map(({ filename = '' }, index) => {
        const needsTruncation = filename.length > 20;
        const displayText = needsTruncation
          ? truncateFilename(filename)
          : filename;

        if (needsTruncation) {
          return (
            <Tooltip key={index} label={filename} placement="top-start">
              <Text width="max-content">{displayText}</Text>
            </Tooltip>
          );
        }

        return (
          <Text key={index} width="max-content">
            {displayText}
          </Text>
        );
      })}
      {hasMore && (
        <Tooltip
          label={t('operations.euq.email.attachment.tooltip', {
            count: MAX_DISPLAYED_ATTACHMENTS,
          })}
          placement="bottom-start"
        >
          <Text width="max-content">......</Text>
        </Tooltip>
      )}
    </>
  );
};

const EmailInfoLoading = () => (
  <>
    <Box mb="4x">
      <Skeleton height="24px" width="80%" animation="wave" />
      <Skeleton height="16px" width="200px" mt="4x" ml="6x" animation="wave" />
    </Box>
    <Stack gap="1x" pl="6x">
      <Skeleton height="5x" width="300px" mb="2x" animation="wave" />
      <Skeleton height="5x" width="350px" mb="2x" animation="wave" />
      <Skeleton height="5x" width="280px" animation="wave" />
    </Stack>
  </>
);

interface EmailInfoContentProps {
  emailData: EmailData;
  formattedTimestamp: string | null;
  secondaryColor: string;
  onViewHeaders: () => void;
}

const EmailInfoContent = ({
  emailData,
  formattedTimestamp,
  secondaryColor,
  onViewHeaders,
}: EmailInfoContentProps) => {
  const formatValue = (value: string | undefined) =>
    value?.split(';').join(', ') || '';

  return (
    <>
      <Box mb="4x">
        <Flex alignItems="start" gap="2x">
          <Icon as={EmailOIcon} size="4x" mt="1x" />
          <Text
            size="lg"
            fontWeight={500}
            wordBreak="break-word"
            whiteSpace="normal"
            flex="1"
          >
            {emailData.msgSubject}
          </Text>
        </Flex>
        {emailData.timestamp && (
          <Text mt="1x" pl="6x" color={secondaryColor}>
            {formattedTimestamp}
          </Text>
        )}
      </Box>

      <Stack gap="1x" pl="6x">
        <InfoRow
          label={t('operations.euq.email.sender')}
          value={formatValue(emailData.sender)}
          secondaryColor={secondaryColor}
        />
        <InfoRow
          label={t('operations.euq.email.recipient')}
          value={formatValue(emailData.recipient)}
          secondaryColor={secondaryColor}
        />
        <InfoRow
          label={t('operations.euq.email.reporter')}
          value={emailData.reporter}
          secondaryColor={secondaryColor}
        />
        <InfoRow
          label={t('operations.euq.email.messageId')}
          value={emailData.messageId}
          secondaryColor={secondaryColor}
        />

        <Flex>
          <Text color={secondaryColor} width="90px">
            {t('operations.euq.email.attachment')}:
          </Text>
          <Stack ml="2x" wordBreak="break-word" whiteSpace="normal" width={200}>
            <AttachmentList attachments={emailData.attachments} />
          </Stack>
        </Flex>

        {emailData.messageHeaders && emailData.messageHeaders.length > 0 && (
          <Flex pt="1x">
            <Box width="90px" />
            <Link onClick={onViewHeaders} cursor="pointer" ml="2x">
              {t('operations.euq.email.viewMessageHeaders')}
            </Link>
          </Flex>
        )}
      </Stack>
    </>
  );
};

interface HeadersViewProps {
  headersText: string;
  secondaryColor: string;
  onBack: () => void;
}

const HeadersView = ({
  headersText,
  secondaryColor,
  onBack,
}: HeadersViewProps) => (
  <Stack height="100%">
    <Button
      variant="ghost"
      columnGap="1x"
      onClick={onBack}
      color={secondaryColor}
      border="none"
      width="max-content"
      px="0"
      height={44}
    >
      <Icon as={ChevronLeftIcon} height="4x" fontWeight="bold" />
      <Text size="md">{t('common.action.label.back')}</Text>
    </Button>
    <Box
      flex={1}
      overflow="auto"
      whiteSpace="pre-wrap"
      wordBreak="break-word"
      fontSize="sm"
    >
      {headersText}
    </Box>
  </Stack>
);

interface EmailContentViewProps {
  emailData: EmailData;
  formattedTimestamp: string | null;
  isLoading: boolean;
  secondaryColor: string;
  onViewHeaders: () => void;
}

const EmailContentView = ({
  emailData,
  formattedTimestamp,
  isLoading,
  secondaryColor,
  onViewHeaders,
}: EmailContentViewProps) => (
  <Stack height="100%">
    {isLoading ? (
      <EmailInfoLoading />
    ) : (
      <EmailInfoContent
        emailData={emailData}
        formattedTimestamp={formattedTimestamp}
        secondaryColor={secondaryColor}
        onViewHeaders={onViewHeaders}
      />
    )}

    <Divider mt="4x" variant="dashed" />

    {isLoading ? (
      <Skeleton height="100%" mt="4x" animation="wave" />
    ) : (
      <Flex mt="4x" flex={1} minHeight={700}>
        <MailRenderer
          htmlContent={emailData.mailHtmlContent}
          textContent={emailData.mailPreviewContent}
          p={0}
          width="100%"
          height="100%"
          imageProxy={{ enabled: true, baseUrl: '/euqFeedBack/image-proxy' }}
        />
      </Flex>
    )}
  </Stack>
);

export const EmailDrawer = ({
  isOpen,
  onClose,
  isLoading,
  emailData,
  formattedTimestamp,
}: EmailDrawerProps) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [showHeaders, setShowHeaders] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const headersText =
    emailData.messageHeaders?.map((h) => h.line).join('\n') || '';

  const handleCopyAll = () => {
    navigator.clipboard.writeText(headersText);
    setCopied(true);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setShowHeaders(false);
    onClose();
  };

  const secondaryColor = colorStyle.color.secondary;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader>
          {showHeaders ? (
            <Flex alignItems="center">
              <Text color={secondaryColor}>
                {t('operations.euq.email.details')}
              </Text>
              <Text mx="2x" color={secondaryColor}>
                ›
              </Text>
              <Text>{t('operations.euq.email.messageHeaders')}</Text>
            </Flex>
          ) : (
            t('operations.euq.email.details')
          )}
        </DrawerHeader>

        <DrawerBody height="100%">
          {showHeaders ? (
            <HeadersView
              headersText={headersText}
              secondaryColor={secondaryColor}
              onBack={() => setShowHeaders(false)}
            />
          ) : (
            <EmailContentView
              emailData={emailData}
              formattedTimestamp={formattedTimestamp}
              isLoading={isLoading}
              secondaryColor={secondaryColor}
              onViewHeaders={() => setShowHeaders(true)}
            />
          )}
        </DrawerBody>

        <DrawerFooter>
          {showHeaders ? (
            <Flex gap="2x">
              <Button onClick={handleCopyAll}>
                {t('common.action.label.copyAll')}
                {copied && <Icon as={CheckSIcon} ml="1x" />}
              </Button>
              <Button onClick={handleClose}>{t('common.button.close')}</Button>
            </Flex>
          ) : (
            <Button onClick={handleClose}>{t('common.button.close')}</Button>
          )}
        </DrawerFooter>

        <DrawerCloseButton />
      </DrawerContent>
    </Drawer>
  );
};
